<script setup lang="ts">
import axios from 'axios'
import { Plus } from 'lucide-vue-next'

import UserModal from './UserModal.vue'
import UsersList from './UsersList.vue'
import UsersRolesList from './UsersRolesList.vue'

const { hasPermissionBySlug } = useAccess()
const { t } = useI18n()
const { stores } = useLocalStorage()

const currentTab = ref(1)
const currentTabString = computed({
  get: () => String(currentTab.value),
  set: (v: string) => {
    currentTab.value = Number(v)
  }
})
const rolesList = ref([])
const isUserCRUDModalOpen = ref(false)
const selectedActionCRUD = ref<ActionsCRUD>('create')
const isResendMailModalOpen = ref(false)

export type ActionsCRUD = 'create' | 'update' | 'delete'

const selectedUser = ref(null)

const canCreate = computed(() => hasPermissionBySlug('permission.create'))

const tabs = [
  { id: 1, name: t('pages.settings.usersPage.tabs.users') },
  { id: 2, name: t('pages.settings.usersPage.tabs.roles') }
]

const getRoles = async () => {
  try {
    const { data } = await axios.get('role-templates')
    rolesList.value = data.data.map((role) => ({
      id: role.roleTemplate.id,
      text: role.roleTemplate.name
    }))
  } catch (error) {
    throw new Error(error)
  }
}

const isUserlistLoaded = ref(true)
const isMailSent = ref(false)

const displayModal = (newModalDisplayState: boolean, cancel = true) => {
  if (!cancel) {
    isUserlistLoaded.value = false
    setTimeout(() => {
      isUserlistLoaded.value = true
    }, 100)
  }
  selectedUser.value = null
  isUserCRUDModalOpen.value = newModalDisplayState
  isResendMailModalOpen.value = false
  isMailSent.value = false
  selectedActionCRUD.value = 'create'
}

const triggerUserAction = (user, action, resendMail = false) => {
  selectedUser.value = user
  selectedActionCRUD.value = action
  isResendMailModalOpen.value = resendMail
  isUserCRUDModalOpen.value = true
}

getRoles()
</script>

<template>
  <div>
    <TheListHeader :title="t('pages.settings.usersPage.title')">
      <template #actions>
        <Button
          v-if="canCreate"
          variant="default-primary"
          class="fixed bottom-0 left-0 z-10 w-screen rounded-none sm:static sm:w-fit sm:rounded-control"
          @click="triggerUserAction(null, 'create')"
        >
          <Plus />
          {{ t('pages.settings.usersPage.addUser') }}
        </Button>
      </template>
    </TheListHeader>
    <Tabs v-model="currentTabString" class="px-6 sm:px-12">
      <TabsList variant="underline">
        <TabsTrigger v-for="tab in tabs" :key="tab.id" variant="underline" :value="String(tab.id)">
          {{ tab.name }}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="1">
        <div v-if="isUserlistLoaded">
          <UsersList
            @select-user:update="triggerUserAction($event, 'update')"
            @select-user:delete="triggerUserAction($event, 'delete')"
            @select-user:resend-invitation="triggerUserAction($event, 'update', true)"
          />
        </div>
        <div v-else class="p-10">
          <AppSkeleton />
        </div>
      </TabsContent>
      <TabsContent value="2">
        <UsersRolesList />
      </TabsContent>
    </Tabs>
  </div>
  <UserModal
    v-if="isUserCRUDModalOpen"
    :is-open="isUserCRUDModalOpen"
    :user-data="selectedUser"
    :stores="stores"
    :roles="rolesList"
    :modal-type="selectedActionCRUD"
    :resend-mail="isResendMailModalOpen"
    @close-modal="displayModal(false, $event)"
  />
</template>
